import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiGet, apiPost } from "../../shared/apiClient";
import { CATEGORIES_API, normalizeCategory } from "../../shared/catalogMastersApi";
import { ImageUploadField } from "../../shared/ImageUploadField";
import { SearchableSelectField } from "../../shared/SearchableSelectField";
import { IconList, IconRefresh, IconSave } from "../../shared/toolbarIcons";

const emptyForm = () => ({
  parentId: "",
  name: "",
  slug: "",
  image: "",
  icon: "",
  order: 0,
  isActive: true,
  isFeatured: false,
  metaTitile: "",
  metaDescription: ""
});

export function CategoriesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const editParam = searchParams.get("edit");

  const [parentsOptions, setParentsOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const loadLookups = async () => {
    setLoading(true);
    setError(null);
    const res = await apiGet(CATEGORIES_API);
    if (res.ok) {
      const list = Array.isArray(res.data) ? res.data.map(normalizeCategory).filter(Boolean) : [];
      setParentsOptions(list);
    } else setError(res.error ?? "Failed to load categories");
    setLoading(false);
  };

  useEffect(() => {
    void loadLookups();
  }, []);

  useEffect(() => {
    if (!editParam) {
      setEditingId(null);
      setForm(emptyForm());
      setLoading(false);
      return;
    }
    const id = Number(editParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const res = await apiGet(`${CATEGORIES_API}/${id}`);
      if (cancelled) return;
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load category");
        return;
      }
      const x = normalizeCategory(res.data);
      if (!x) return;
      setEditingId(x.id);
      setForm({
        parentId: x.parentId ?? "",
        name: x.name ?? "",
        slug: x.slug ?? "",
        image: x.image ?? "",
        icon: x.icon ?? "",
        order: x.order ?? 0,
        isActive: x.isActive,
        isFeatured: x.isFeatured,
        metaTitile: x.metaTitile ?? "",
        metaDescription: x.metaDescription ?? ""
      });
    })();
    return () => {
      cancelled = true;
      setLoading(false);
    };
  }, [editParam]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm());
    setSearchParams({}, { replace: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    setError(null);
    const res = await apiPost(CATEGORIES_API, {
      id: editingId ?? 0,
      parentId: form.parentId ? Number(form.parentId) : null,
      name: form.name,
      slug: form.slug || null,
      image: form.image || null,
      icon: form.icon || null,
      order: Number(form.order || 0),
      isActive: form.isActive,
      isFeatured: form.isFeatured
    });
    if (!res.ok) setError(res.error ?? "Failed to save category");
    else {
      await loadLookups();
      resetForm();
    }
    setSaving(false);
  };

  const onRefresh = async () => {
    setError(null);
    await loadLookups();
    if (editParam) {
      const id = Number(editParam);
      if (!Number.isFinite(id)) return;
      setLoading(true);
      const res = await apiGet(`${CATEGORIES_API}/${id}`);
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load category");
        return;
      }
      const x = normalizeCategory(res.data);
      if (!x) return;
      setEditingId(x.id);
      setForm({
        parentId: x.parentId ?? "",
        name: x.name ?? "",
        slug: x.slug ?? "",
        image: x.image ?? "",
        icon: x.icon ?? "",
        order: x.order ?? 0,
        isActive: x.isActive,
        isFeatured: x.isFeatured,
        metaTitile: x.metaTitile ?? "",
        metaDescription: x.metaDescription ?? ""
      });
    } else {
      setEditingId(null);
      setForm(emptyForm());
    }
  };

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>{editingId ? "Edit Category" : "Add Category"}</h2>
        </div>
        <div className="card-header-actions">
          <button
            className="icon-button icon-button--toolbar"
            type="submit"
            form="categories-form"
            disabled={saving}
            title="Save"
            aria-label="Save category"
          >
            <IconSave />
          </button>
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void onRefresh()}
            disabled={saving || loading}
            title="Refresh"
            aria-label="Refresh"
          >
            <IconRefresh />
          </button>
          <Link to="/categories-list" className="icon-button icon-button--toolbar" title="Category list" aria-label="Open category list">
            <IconList />
          </Link>
        </div>
      </div>

      <div className="card-body">
        <form id="categories-form" onSubmit={onSubmit} className="form form-fields-bottomline">
          <div className="form-row form-row--4">
            <label className="field">
              <span>Name</span>
              <input type="text" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            </label>
            <label className="field">
              <span>Slug</span>
              <input type="text" value={form.slug} onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))} />
            </label>
            <SearchableSelectField
              label="Parent Category"
              options={parentsOptions.filter((x) => x.id !== editingId)}
              value={form.parentId}
              onChange={(nextValue) => setForm((p) => ({ ...p, parentId: nextValue === "" ? "" : Number(nextValue) }))}
              getOptionLabel={(option) => option.name || `Category #${option.id}`}
              getSearchText={(option) => `${option.name ?? ""} ${option.slug ?? ""} ${option.id ?? ""}`}
              placeholder="Search parent category"
              noOptionsText="No categories found"
            />
            <label className="field">
              <span>Order</span>
              <input type="number" value={form.order} onChange={(e) => setForm((p) => ({ ...p, order: e.target.value }))} />
            </label>
          </div>
          <div className="form-row form-row--4">
            <label className="field">
              <span>Meta Title</span>
              <input type="text" value={form.metaTitile} onChange={(e) => setForm((p) => ({ ...p, metaTitile: e.target.value }))} />
            </label>
            <label className="field">
              <span>Meta Description</span>
              <input type="text" value={form.metaDescription} onChange={(e) => setForm((p) => ({ ...p, metaDescription: e.target.value }))} />
            </label>
            <ImageUploadField label="Image" value={form.image} onChange={(image) => setForm((p) => ({ ...p, image }))} />
          </div>
          <div className="form-row form-row--4">
            <ImageUploadField label="Icon" value={form.icon} onChange={(icon) => setForm((p) => ({ ...p, icon }))} />
          </div>
          {error && <div className="error-banner">{error}</div>}
        </form>
      </div>
    </div>
  );
}
